import { useState } from "react";
import { CheckCircle2, Loader2, Mic, ShieldCheck, Volume2, Waves } from "lucide-react";
import LevelMeter from "@/components/recstudio/setup/LevelMeter";
import { levelAdvice, noiseAdvice } from "@/lib/audioAnalysis";

function TestCard({ id, icon: Icon, title, hint, secs, running, result, advice, onRun, disabled }) {
  return (
    <div data-testid={`setup-${id}`} className="rounded-2xl border border-white/[0.08] bg-black/25 p-4">
      <p className="flex items-center gap-2 font-display text-[15px] font-semibold"><Icon className="h-4 w-4 text-rose-200" /> {title}</p>
      <p className="mt-1 text-[12px] text-white/55">{hint}</p>
      <button type="button" data-testid={`setup-${id}-btn`} onClick={onRun} disabled={disabled || running}
        className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-[13px] transition-colors duration-200 hover:bg-white/[0.12] disabled:opacity-40">
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}{running ? `Listening… ${secs}s` : result ? "Test again" : "Start test"}
      </button>
      {advice ? (
        <p data-testid={`setup-${id}-result`} data-ok={advice.ok ? "1" : "0"} className={`mt-3 text-[13px] ${advice.ok ? "text-emerald-200" : "text-amber-200"}`}>
          <span className="font-mono text-[11px] text-white/50">{result} · </span>{advice.text}
        </p>
      ) : null}
    </div>
  );
}

/** Step 1 — permission, microphone choice, live meter, quiet-room + level tests, READY. */
export default function MicSetupStep({ mic, results, setResults }) {
  const [running, setRunning] = useState(null);
  const live = mic.state === "live";
  const run = async (key, sec) => {
    setRunning(key);
    mic.resetClip();
    const m = await mic.measure(sec);
    setRunning(null);
    if (!m) return;
    setResults((r) => ({ ...r, [key]: key === "noise" ? m.noiseDb : m.peakDb, clippedInTest: key === "level" ? mic.clipped : r.clippedInTest }));
  };
  const noise = noiseAdvice(results.noise);
  const level = levelAdvice(results.level);
  return (
    <div className="space-y-5">
      {!live ? (
        <div data-testid="setup-permission" className="rounded-3xl border border-rose-300/25 bg-rose-500/[0.07] p-6">
          <p className="font-display text-lg font-semibold">First, let ELPM hear your microphone</p>
          <p className="mt-1 text-[13px] text-white/60">Your browser will ask for permission — click <b>Allow</b>. Nothing is recorded during setup.</p>
          <button type="button" data-testid="setup-allow-mic" onClick={() => mic.open()} disabled={mic.state === "asking"}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 px-6 py-3 font-display font-semibold disabled:opacity-60">
            {mic.state === "asking" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {mic.state === "asking" ? "Waiting for you to click Allow…" : "Allow microphone"}
          </button>
        </div>
      ) : (
        <div className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5">
          <label className="block">
            <span className="mb-1.5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/60"><Mic className="h-3.5 w-3.5" /> Microphone</span>
            <select data-testid="setup-mic-select" value={mic.deviceId} onChange={(e) => mic.open(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white focus:border-rose-300/60 focus:outline-none">
              {mic.devices.map((d) => <option key={d.deviceId || d.label} value={d.deviceId}>{d.label}</option>)}
            </select>
          </label>
          <p className="mb-2 mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/60"><Volume2 className="h-3.5 w-3.5" /> Live input — sing or speak</p>
          <LevelMeter testId="setup-meter" big peakDb={mic.levels.peakDb} rmsDb={mic.levels.rmsDb} clipped={mic.clipped} onResetClip={mic.resetClip} />
          {mic.clipped ? <p data-testid="setup-clip-warning" className="mt-2 text-[12px] text-rose-200">That was too loud and clipped — back off a little or lower the mic's input gain.</p> : null}
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <TestCard id="noise" icon={Waves} title="Quiet-room check" hint="Stay silent for 3 seconds so we can hear the room." secs={3} running={running === "noise"} disabled={!live || running}
          result={results.noise != null ? `${results.noise.toFixed(0)} dB room noise` : null} advice={noise} onRun={() => run("noise", 3)} />
        <TestCard id="level" icon={Mic} title="Level check" hint="Sing your loudest line for 5 seconds, as you would on the song." secs={5} running={running === "level"} disabled={!live || running}
          result={results.level != null ? `${results.level.toFixed(1)} dB peak` : null} advice={level} onRun={() => run("level", 5)} />
      </div>
      {live && level?.ok ? <p data-testid="setup-step1-ready" className="flex items-center gap-2 text-sm text-emerald-200"><CheckCircle2 className="h-4 w-4" /> Microphone ready</p> : null}
    </div>
  );
}
